import React, { useEffect, useMemo, useState } from 'react'
import { Alert, Badge, Button, Card, Empty, Input, Layout, Select, Spin, Table, Tag, Timeline, Typography } from 'antd'
import { ApiOutlined, ArrowLeftOutlined, SearchOutlined } from '@ant-design/icons'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import { getAicDashboardSummary, getAicDashboardDatasets, getAicDashboardDatasetDetail, getAicDashboardExceptions } from '../../request'
import VibeCodexGuideDialog from '../vibe/VibeCodexGuideDialog'
import { VIBE_CODEX_GUIDE_SCENE } from '../vibe/vibeCodexGuide'
import AicReliabilitySettings from './AicReliabilitySettings'
import {
  display,
  formatDateTime,
  formatUpdateFrequency,
  getHealthStatus,
  healthMeta,
  healthyRateSummary,
  settleRequest,
  unwrapList
} from './aicDashboardUtils'
import './index.less'

const pendingText = <span className="aic-pending-result">待配置并执行规则</span>
const waitingText = <span className="aic-pending-result">待检测</span>
const AIC_SKILL_MARKET_PATH = '/portal/profile?tab=skillmarket'

const importPeriodLabels = {
  daily: '日',
  weekly: '周',
  monthly: '月',
  irregular: '不定期'
}

const scheduleStatusLabels = {
  pending: '待上传',
  satisfied: '已按期上传',
  overdue: '已超期',
  cancelled: '已取消'
}

const validationStatusLabels = {
  passed: '校验通过',
  valid: '校验通过',
  failed: '校验失败',
  pending: '待校验'
}

const reliabilityStatusMeta = status => {
  if (status === 'healthy') return { status: 'success', label: '正常' }
  if (status === 'warning') return { status: 'warning', label: '预警' }
  if (status === 'check_failed') return { status: 'error', label: '执行失败' }
  if (status === 'unknown') return { status: 'default', label: '暂无数据' }
  return { status: 'default', label: '待检测' }
}

const qualityResultText = item => {
  if (item.qualityStatus === 'healthy') return '未发现异常数据'
  if (item.qualityStatus === 'warning') return `发现 ${item.qualityErrorCount ?? 0} 条异常数据`
  if (item.qualityStatus === 'check_failed') return display(item.qualityErrorSummary || item.qualityErrorCode || '规则执行失败')
  if (item.qualityStatus === 'unknown') return '本次检测未取得有效结果'
  return '规则配置并执行后展示真实结果'
}

function Nav ({ active }) {
  const history = useHistory()
  const links = [
    ['overview', '总览', '/portal/aic-dashboard/overview', false],
    ['datasets', '数据源', '/portal/aic-dashboard/datasets', false],
    ['exceptions', '异常中心', '/portal/aic-dashboard/exceptions', false],
    ['settings', '设置', '/portal/aic-dashboard/settings', true]
  ]
  return <div className="aic-nav">
    <div className="aic-logo">AIC</div>
    <div className="aic-nav-links">
      {links.map(([key, label, path, disabled]) => <button
        key={key}
        className={!disabled && active === key ? 'active' : ''}
        disabled={disabled}
        title={disabled ? '暂未开放' : undefined}
        onClick={() => history.push(path)}
      >
        {label}
        {disabled && <span className="aic-nav-disabled-note">暂未开放</span>}
      </button>)}
    </div>
    <Button
      className="aic-nav-skill-entry"
      type="primary"
      icon={<ApiOutlined />}
      aria-label="前往 Skill 市场获取 AIC Skill"
      onClick={() => history.push(AIC_SKILL_MARKET_PATH)}
    >
      <span className="aic-nav-skill-label"><span className="aic-nav-skill-prefix">获取 </span>AIC Skill</span>
    </Button>
  </div>
}

function Header ({ title, subtitle, active }) {
  return <>
    <Nav active={active} />
    <div className="aic-page-header">
      <Typography.Title level={1}>{title}</Typography.Title>
      {subtitle && <Typography.Paragraph type="secondary">{subtitle}</Typography.Paragraph>}
    </div>
  </>
}

function Overview ({ summary, datasets, exceptions }) {
  const history = useHistory()
  const attention = exceptions.slice(0, 5)
  const datasetCount = summary.datasetCount ?? datasets.length
  return <div>
    <Header
      active="overview"
      title="数据可靠性"
      subtitle={<>{healthyRateSummary(summary.healthyRate)} · {display(datasetCount)} 个数据源 · {exceptions.length} 个异常</>}
    />
    <div className="aic-overview-panels">
      <Card className="aic-panel aic-overview-attention" title="需要关注" extra={<Button type="link" onClick={() => history.push('/portal/aic-dashboard/exceptions')}>查看异常中心</Button>}>
        <Table
          size="small"
          pagination={false}
          dataSource={attention}
          rowKey={row => row.id || row.projectId}
          columns={[
            { title: '问题', render: (_, row) => display(row.title || row.type) },
            { title: '状态', dataIndex: 'status', render: display },
            { title: '影响', dataIndex: 'impact', render: display },
            { title: '负责人', dataIndex: 'owner', render: display },
            { title: '操作', render: () => <Button type="link" onClick={() => history.push('/portal/aic-dashboard/exceptions')}>查看</Button> }
          ]}
          locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="当前没有待关注异常" /> }}
        />
      </Card>
      <Card className="aic-panel aic-overview-trend" title="健康率趋势">
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无健康率趋势数据" />
      </Card>
    </div>
  </div>
}

function Catalog ({ datasets }) {
  const history = useHistory()
  const [keyword, setKeyword] = useState('')
  const [status, setStatus] = useState('all')
  const [sensitivity, setSensitivity] = useState('all')
  const filtered = useMemo(() => datasets.filter(dataset => (
    (!keyword || [dataset.projectName, dataset.owner, dataset.ownerDepartmentName, dataset.businessSubject].join(' ').toLowerCase().includes(keyword.toLowerCase())) &&
    (status === 'all' || getHealthStatus(dataset) === status) &&
    (sensitivity === 'all' || dataset.sensitivityLevel === sensitivity)
  )), [datasets, keyword, status, sensitivity])
  const columns = [
    { title: '数据源名称', dataIndex: 'projectName', render: display },
    { title: '部门', dataIndex: 'ownerDepartmentName', render: display },
    {
      title: '健康状态',
      render: (_, row) => {
        const meta = healthMeta(getHealthStatus(row))
        return <Badge status={meta.status} text={meta.label} />
      }
    },
    { title: '最后上传时间', dataIndex: 'lastSuccessfulUploadAt', width: 180, render: value => formatDateTime(value) },
    { title: '计划截止时间', dataIndex: 'nextExpectedUploadAt', width: 180, render: value => formatDateTime(value, '-') },
    {
      title: '最新数据截止日期', dataIndex: 'latestDataDate', width: 170,
      render: (value, row) => row.latestDataDateStatus ? formatDateTime(value, row.latestDataDateStatus === 'unknown' ? '暂无数据' : '-') : pendingText
    },
    { title: '异常数据', dataIndex: 'qualityErrorCount', width: 95, render: (value, row) => row.qualityStatus ? (value ?? '-') : waitingText },
    {
      title: '质量状态', dataIndex: 'qualityStatus', width: 105,
      render: value => {
        const meta = reliabilityStatusMeta(value)
        return <Badge status={meta.status} text={meta.label} />
      }
    },
    { title: '负责人', dataIndex: 'owner', render: display },
    { title: '操作', width: 70, render: (_, row) => <Button type="link" onClick={() => history.push(`/portal/aic-dashboard/datasets/${row.projectId}`)}>详情</Button> }
  ]
  return <div>
    <Nav active="datasets" />
    <div className="aic-toolbar">
      <Input allowClear prefix={<SearchOutlined />} placeholder="搜索数据源、部门或负责人" value={keyword} onChange={event => setKeyword(event.target.value)} />
      <Select className="aic-filter-select" value={status} onChange={setStatus} options={[
        ['all', '全部健康状态'], ['healthy', '健康'], ['warning', '预警'], ['failing', '故障'], ['unknown', '未知']
      ].map(([value, label]) => ({ value, label }))} />
      <Select className="aic-filter-select" value={sensitivity} onChange={setSensitivity} options={[
        { value: 'all', label: '全部敏感级' }, { value: 'L1', label: 'L1 · 全员可见' }, { value: 'L2', label: 'L2 · 部门可见' }, { value: 'L3', label: 'L3 · 授权可见' }
      ]} />
    </div>
    <Card className="aic-panel" title={`我可见的数据源（${filtered.length}/${datasets.length}）`}>
      <Table
        rowKey={row => row.datasetId || row.projectId}
        columns={columns}
        dataSource={filtered}
        scroll={{ x: 1320 }}
        pagination={{ pageSize: 10, showSizeChanger: false }}
        locale={{ emptyText: <Empty description="当前没有符合条件的数据源" /> }}
      />
    </Card>
  </div>
}

function Detail () {
  const { projectId } = useParams()
  const history = useHistory()
  const [activeAnchor, setActiveAnchor] = useState('health')
  const [item, setItem] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [codexGuideVisible, setCodexGuideVisible] = useState(false)
  const anchors = [['health', '健康'], ['reliability', '可靠性规则'], ['quality', '业务数据检测'], ['structure', '结构'], ['runtime', '运行'], ['audit', '权限与审计']]

  useEffect(() => {
    let mounted = true
    setLoading(true)
    setError('')
    getAicDashboardDatasetDetail(projectId)
      .then(value => { if (mounted) setItem(value && value.projectId ? value : null) })
      .catch(() => { if (mounted) setError('数据源详情加载失败') })
      .finally(() => { if (mounted) setLoading(false) })
    return () => { mounted = false }
  }, [projectId])

  const jumpTo = key => {
    setActiveAnchor(key)
    const target = document.getElementById(`aic-detail-${key}`)
    if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  if (loading) return <><Nav active="datasets" /><div className="aic-detail-loading"><Spin /></div></>
  if (!item) return <><Nav active="datasets" />{error && <Alert type="error" showIcon message={error} />}<Card className="aic-panel"><Empty description="数据源不存在或无法读取" /><Button onClick={() => history.push('/portal/aic-dashboard/datasets')}>返回数据源</Button></Card></>

  return <div>
    <Nav active="datasets" />
    <div className="aic-detail-head">
      <Button type="link" icon={<ArrowLeftOutlined />} onClick={() => history.push('/portal/aic-dashboard/datasets')}>数据源</Button>
      <Typography.Title level={1}>{display(item.projectName)}</Typography.Title>
      <div className="aic-detail-meta"><span>负责人：{display(item.owner)}</span></div>
    </div>
    <nav className="aic-tabs aic-anchor-tabs" aria-label="数据源详情导航">
      {anchors.map(([key, label]) => <button key={key} className={activeAnchor === key ? 'active' : ''} onClick={() => jumpTo(key)} aria-current={activeAnchor === key ? 'location' : undefined}>{label}</button>)}
    </nav>
    <div id="aic-detail-health" className="aic-detail-section"><DetailTab type="health" item={item} /></div>
    <div id="aic-detail-reliability" className="aic-detail-section"><DetailTab type="reliability" item={item} /></div>
    <div id="aic-detail-quality" className="aic-detail-section"><DetailTab type="quality" item={item} /></div>
    <div id="aic-detail-structure" className="aic-detail-section"><DetailTab type="structure" item={item} /></div>
    <div id="aic-detail-runtime" className="aic-detail-section"><DetailTab type="runtime" item={item} /></div>
    <div id="aic-detail-audit" className="aic-detail-section"><DetailTab type="audit" item={item} onOpenCodexGuide={() => setCodexGuideVisible(true)} /></div>
    <VibeCodexGuideDialog visible={codexGuideVisible} scene={VIBE_CODEX_GUIDE_SCENE.NORMAL} onClose={() => setCodexGuideVisible(false)} />
  </div>
}

function DetailTab ({ type, item, onOpenCodexGuide }) {
  if (type === 'reliability') return <ReliabilityRules item={item} />
  if (type === 'quality') {
    const qualityMeta = reliabilityStatusMeta(item.qualityStatus)
    const latestDataDate = item.latestDataDateStatus
      ? formatDateTime(item.latestDataDate, item.latestDataDateStatus === 'unknown' ? '暂无数据' : '-')
      : pendingText
    return <Card className="aic-panel" title="业务数据检测">
      <div className="aic-description-grid">
        <span>最新数据截止日期</span><b>{latestDataDate}</b>
        <span>数据日期检测时间</span><b>{item.latestDataDateStatus ? formatDateTime(item.latestDataDateCheckedAt, '-') : waitingText}</b>
        <span>异常数据数量</span><b>{item.qualityStatus ? (item.qualityErrorCount ?? '-') : waitingText}</b>
        <span>质量状态</span><b><Badge status={qualityMeta.status} text={qualityMeta.label} /></b>
        <span>质量检测时间</span><b>{item.qualityStatus ? formatDateTime(item.qualityCheckedAt, '-') : waitingText}</b>
        <span>检测结果</span><b>{qualityResultText(item)}</b>
      </div>
    </Card>
  }
  if (type === 'structure') {
    const fields = Array.isArray(item.fields) ? item.fields : []
    return <Card className="aic-panel" title="字段字典">
      <Table
        size="small"
        pagination={false}
        rowKey={field => field.fieldId || `${field.tableId}-${field.fieldName}`}
        columns={[
          { title: '字段名', dataIndex: 'fieldName', render: display },
          { title: '业务含义', render: (_, field) => display(field.description || field.displayName) },
          { title: '类型', dataIndex: 'dataType', render: display },
          { title: '质量规则', dataIndex: 'qualityConfigured', render: configured => <Badge status={configured ? 'success' : 'default'} text={configured ? '已配置' : '未配置'} /> }
        ]}
        dataSource={fields}
        locale={{ emptyText: <Empty description="从未上传过数据" /> }}
      />
    </Card>
  }
  if (type === 'runtime') return <Card className="aic-panel" title="运行记录"><Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="数据导入类，无运行记录" /></Card>
  if (type === 'audit') return <Card className="aic-panel" title="权限与审计">
    <div className="aic-description-grid">
      <span>数据级别</span><b>{display(item.sensitivityLevel)}</b>
      <span>访问方式</span><b><Button type="primary" size="small" onClick={onOpenCodexGuide}>通过 Codex/Mission 使用</Button></b>
      <span>最近审计</span><b>无</b>
      <span>审计结论</span><b>无</b>
    </div>
  </Card>
  const meta = healthMeta(getHealthStatus(item))
  return <Card className="aic-panel" title="关键信息">
    <div className="aic-description-grid">
      <span>部门</span><b>{display(item.ownerDepartmentName)}</b>
      <span>敏感级</span><b>{display(item.sensitivityLevel)}</b>
      <span>健康状态</span><b><Badge status={meta.status} text={meta.label} /></b>
      <span>DELAY 状态</span><b>{item.healthReason ? display(item.healthReason) : meta.label}</b>
      <span>最后上传时间</span><b>{formatDateTime(item.lastSuccessfulUploadAt)}</b>
      <span>计划截止时间</span><b>{formatDateTime(item.nextExpectedUploadAt, '-')}</b>
      <span>更新频率</span><b>{formatUpdateFrequency(item.updateFrequency)}</b>
      <span>超期时长</span><b>{item.overdueHours === null || item.overdueHours === undefined ? '-' : `${item.overdueHours} 小时`}</b>
    </div>
  </Card>
}

function ReliabilityRules ({ item }) {
  const config = item.reliabilityConfig || {}
  const importPlan = config.importPlan
  const dataDateField = config.dataDateField
  const qualityRules = Array.isArray(config.qualityRules) ? config.qualityRules : []
  const schedules = importPlan && Array.isArray(importPlan.schedules) ? importPlan.schedules.slice(0, 5) : []
  const qualityMeta = reliabilityStatusMeta(item.qualityStatus)

  return <Card className="aic-panel aic-reliability-panel" title="可靠性规则" extra={<Tag color="blue">只读</Tag>}>
    <Alert
      className="aic-readonly-note"
      type="info"
      showIcon
      message="展示当前已启用规则"
      description="这里只展示已经确认并参与监控的版本，不包含草稿、历史版本和生成 SQL。"
    />

    <section className="aic-reliability-block">
      <div className="aic-reliability-block-head">
        <div><h3>导入计划</h3><span>用于判断数据是否按计划上传</span></div>
        {importPlan && <Tag color="green">v{importPlan.version} · 已启用</Tag>}
      </div>
      {importPlan ? <>
        <div className="aic-description-grid aic-rule-description-grid">
          <span>导入周期</span><b>{display(importPeriodLabels[importPlan.periodType] || importPlan.periodType)}</b>
          <span>时区</span><b>{display(importPlan.timezone)}</b>
          <span>规则说明</span><b>{display(importPlan.naturalLanguageRule)}</b>
          <span>宽限时间</span><b>{importPlan.gracePeriodHours === null || importPlan.gracePeriodHours === undefined ? '-' : `${importPlan.gracePeriodHours} 小时`}</b>
          <span>当前计划截止</span><b>{formatDateTime(item.nextExpectedUploadAt, '-')}</b>
          <span>确认时间</span><b>{formatDateTime(importPlan.confirmedAt, '-')}</b>
        </div>
        {schedules.length > 0 && <div className="aic-readonly-table">
          <div className="aic-readonly-table-title">导入计划（展示前 {schedules.length} 期，共 {importPlan.schedules.length} 期）</div>
          <Table
            size="small"
            pagination={false}
            rowKey={row => row.scheduleId || row.sequenceNo}
            dataSource={schedules}
            columns={[
              { title: '期次', dataIndex: 'periodKey', render: display },
              { title: '周期开始', dataIndex: 'windowStartAt', render: value => formatDateTime(value, '-') },
              { title: '导入截止', dataIndex: 'deadlineAt', render: value => formatDateTime(value, '-') },
              { title: '宽限截止', dataIndex: 'graceDeadlineAt', render: value => formatDateTime(value, '-') },
              { title: '状态', dataIndex: 'status', render: value => display(scheduleStatusLabels[value] || value) }
            ]}
          />
        </div>}
      </> : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="尚未启用导入计划" />}
    </section>

    <section className="aic-reliability-block">
      <div className="aic-reliability-block-head">
        <div><h3>数据日期字段</h3><span>字段最大值代表该数据源的最新业务日期</span></div>
        {dataDateField && <Tag color="green">v{dataDateField.version} · 已启用</Tag>}
      </div>
      {dataDateField ? <div className="aic-description-grid aic-rule-description-grid">
        <span>逻辑表</span><b>{tableDisplay(dataDateField)}</b>
        <span>数据日期字段</span><b>{fieldDisplay(dataDateField)}</b>
        <span>字段类型</span><b>{display(dataDateField.fieldDataType)}</b>
        <span>日期粒度</span><b>{display(dataDateField.granularity)}</b>
        <span>取值方式</span><b>取该字段最大值</b>
        <span>最新数据日期</span><b>{item.latestDataDateStatus ? formatDateTime(item.latestDataDate, '暂无数据') : '待执行'}</b>
        <span>安全校验</span><b>{display(validationStatusLabels[dataDateField.validationStatus] || dataDateField.validationStatus)}</b>
        <span>最近检测</span><b>{formatDateTime(item.latestDataDateCheckedAt, '-')}</b>
      </div> : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="尚未启用数据日期字段" />}
    </section>

    <section className="aic-reliability-block">
      <div className="aic-reliability-block-head">
        <div><h3>字段质量预警</h3><span>命中规则的数据将进入异常判断</span></div>
        {qualityRules.length > 0 && <Badge status={qualityMeta.status} text={`${qualityRules.length} 条已启用规则`} />}
      </div>
      {qualityRules.length > 0 ? <>
        <div className="aic-quality-result-summary">
          <span>最近检测结果</span>
          <b><Badge status={qualityMeta.status} text={qualityResultText(item)} /></b>
          <span>检测时间</span>
          <b>{formatDateTime(item.qualityCheckedAt, '-')}</b>
        </div>
        <div className="aic-readonly-table">
          <Table
            size="small"
            pagination={false}
            rowKey={row => row.ruleId || row.ruleKey}
            dataSource={qualityRules}
            columns={[
              { title: '规则名称', dataIndex: 'ruleName', render: display },
              { title: '逻辑表', render: (_, row) => tableDisplay(row) },
              { title: '字段', render: (_, row) => fieldDisplay(row) },
              { title: '规则说明', dataIndex: 'description', render: display },
              { title: '级别', dataIndex: 'severity', render: display },
              { title: '版本', dataIndex: 'version', render: value => value ? `v${value}` : '-' },
              { title: '安全校验', dataIndex: 'validationStatus', render: value => display(validationStatusLabels[value] || value) }
            ]}
          />
        </div>
      </> : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="尚未启用字段质量预警规则" />}
    </section>
  </Card>
}

function tableDisplay (rule) {
  if (!rule) return '-'
  if (rule.tableDisplayName && rule.tableName && rule.tableDisplayName !== rule.tableName) return `${rule.tableDisplayName}（${rule.tableName}）`
  return display(rule.tableDisplayName || rule.tableName)
}

function fieldDisplay (rule) {
  if (!rule) return '-'
  if (rule.fieldDisplayName && rule.fieldName && rule.fieldDisplayName !== rule.fieldName) return `${rule.fieldDisplayName}（${rule.fieldName}）`
  return display(rule.fieldDisplayName || rule.fieldName)
}

function Exceptions ({ exceptions }) {
  const [selectedId, setSelectedId] = useState(exceptions[0] && exceptions[0].id)
  const [keyword, setKeyword] = useState('')
  const [type, setType] = useState('all')
  const [status, setStatus] = useState('all')
  const [owner, setOwner] = useState('all')
  const typeOptions = useMemo(() => Array.from(new Set(exceptions.map(item => item.type).filter(Boolean))), [exceptions])
  const statusOptions = useMemo(() => Array.from(new Set(exceptions.map(item => item.status).filter(Boolean))), [exceptions])
  const ownerOptions = useMemo(() => Array.from(new Set(exceptions.map(item => item.owner).filter(Boolean))), [exceptions])
  const filtered = exceptions.filter(item => (
    (!keyword || [item.title, item.owner, item.impact, item.healthReason].join(' ').toLowerCase().includes(keyword.toLowerCase())) &&
    (type === 'all' || item.type === type) &&
    (status === 'all' || item.status === status) &&
    (owner === 'all' || item.owner === owner)
  ))
  const selected = filtered.find(item => String(item.id) === String(selectedId)) || filtered[0]
  const evidence = selected && Array.isArray(selected.evidence) ? selected.evidence : []
  const timeline = selected && Array.isArray(selected.timeline) ? selected.timeline : []
  return <div>
    <Nav active="exceptions" />
    <div className="aic-exception-toolbar">
      <Select value={type} onChange={setType} options={[{ value: 'all', label: '全部类型' }, ...typeOptions.map(value => ({ value, label: value }))]} />
      <Select value={status} onChange={setStatus} options={[{ value: 'all', label: '全部状态' }, ...statusOptions.map(value => ({ value, label: value }))]} />
      <Select value={owner} onChange={setOwner} options={[{ value: 'all', label: '全部负责人' }, ...ownerOptions.map(value => ({ value, label: value }))]} />
      <Input value={keyword} onChange={event => setKeyword(event.target.value)} prefix={<SearchOutlined />} placeholder="搜索异常、影响或负责人" allowClear />
    </div>
    <Alert className="aic-readonly-note" showIcon type="info" message="异常中心当前为只读视图" description="接手、恢复和通知历史尚无后端操作接口，页面不会伪造处理结果。" />
    <div className="aic-exception-layout">
      <Card className="aic-exception-list" title={`异常列表（${filtered.length}）`}>
        {filtered.length === 0 && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无异常记录" />}
        {filtered.map(item => <button key={item.id} className={`aic-exception-item ${selected && selected.id === item.id ? 'selected' : ''}`} onClick={() => setSelectedId(item.id)}>
          <div className="aic-exception-item-title">{display(item.title)}</div>
          <div className="aic-exception-item-meta"><Tag color={item.severity === 'critical' || item.severity === '高' ? 'red' : 'orange'}>{display(item.severity)}</Tag><span>{display(item.status)}</span><span>{formatDateTime(item.lastSeenAt, '-')}</span></div>
        </button>)}
      </Card>
      <Card className="aic-exception-detail" title="异常详情">
        {selected ? <>
          <div className="aic-detail-title-row"><Typography.Title level={3}>{display(selected.title)}</Typography.Title><Tag color={selected.severity === 'critical' || selected.severity === '高' ? 'red' : 'orange'}>{display(selected.severity)}风险</Tag></div>
          <div className="aic-description-grid">
            <span>异常类型</span><b>{display(selected.type)}</b>
            <span>影响范围</span><b>{display(selected.impact)}</b>
            <span>负责人</span><b>{display(selected.owner)}</b>
            <span>当前状态</span><b>{display(selected.status)}</b>
            <span>最后上传时间</span><b>{formatDateTime(selected.lastSuccessfulUploadAt)}</b>
            <span>计划截止时间</span><b>{formatDateTime(selected.nextExpectedUploadAt, '-')}</b>
            <span>首次发现</span><b>{formatDateTime(selected.firstSeenAt, '-')}</b>
            <span>最近发现</span><b>{formatDateTime(selected.lastSeenAt, '-')}</b>
            <span>恢复时间</span><b>{formatDateTime(selected.recoveredAt, '未恢复')}</b>
            <span>超期时长</span><b>{selected.overdueHours === null || selected.overdueHours === undefined ? '-' : `${selected.overdueHours} 小时`}</b>
          </div>
          <Card size="small" title="判定依据"><ul>{evidence.length > 0 ? evidence.map((value, index) => <li key={`evidence-${index}`}>{display(value)}</li>) : <li>暂无判定依据</li>}</ul></Card>
          {timeline.length > 0 && <Timeline className="aic-timeline" items={timeline.map((value, index) => ({ key: `timeline-${index}`, children: display(value) }))} />}
        </> : <Empty description="暂无异常记录" />}
      </Card>
    </div>
  </div>
}

export default function AicDashboard () {
  const location = useLocation()
  const [summary, setSummary] = useState({})
  const [datasets, setDatasets] = useState([])
  const [exceptions, setExceptions] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    Promise.all([
      settleRequest(getAicDashboardSummary({})),
      settleRequest(getAicDashboardDatasets({})),
      settleRequest(getAicDashboardExceptions({}))
    ])
      .then(([summaryResult, datasetResult, exceptionResult]) => {
        if (summaryResult.ok) setSummary(summaryResult.value || {})
        if (datasetResult.ok) setDatasets(unwrapList(datasetResult.value))
        if (exceptionResult.ok) setExceptions(unwrapList(exceptionResult.value))
        const failed = [
          !summaryResult.ok && '总览',
          !datasetResult.ok && '数据源',
          !exceptionResult.ok && '异常中心'
        ].filter(Boolean)
        setError(failed.length > 0 ? `${failed.join('、')}数据加载失败，其余内容仍可使用` : '')
      })
      .finally(() => setLoading(false))
  }, [])

  const path = location.pathname
  const page = path.includes('/settings')
    ? 'settings'
    : path.includes('/exceptions')
      ? 'exceptions'
      : path.match(/\/datasets\/[^/]+/)
        ? 'detail'
        : path.includes('/datasets') ? 'datasets' : 'overview'

  return <Layout className="aic-dashboard">
    <Spin spinning={loading}>
      {error && <Alert type="warning" showIcon message={error} />}
      {page === 'overview' && <Overview summary={summary} datasets={datasets} exceptions={exceptions} />}
      {page === 'datasets' && <Catalog datasets={datasets} />}
      {page === 'detail' && <Detail />}
      {page === 'exceptions' && <Exceptions exceptions={exceptions} />}
      {page === 'settings' && <AicReliabilitySettings datasets={datasets} Nav={Nav} />}
    </Spin>
  </Layout>
}
